/**
 * @fileoverview Popover with exception toggles for payment requirements.
 * @module features/payments/components/PaymentRequirementsPopover
 */

import { type ReactElement, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { EllipsisVertical } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { updatePaymentLinkingRequirement } from '@/features/payments/api/linkingRequirementService'
import { overrideUniversalTransaction } from '@/features/universal-transactions/api/universalTransactions'
import type { PaymentDTO } from '@/features/payments/schemas/paymentsSchema'

interface PaymentRequirementsPopoverProps {
  payment: PaymentDTO
  className?: string
}

export default function PaymentRequirementsPopover({
  payment,
  className,
}: PaymentRequirementsPopoverProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const invoiceNotNeeded = payment.invoiceStatus === 'INVOICE_NOT_NEEDED'
  const costLinkNotNeeded = payment.costLinkingRequirement === 'NOT_NEEDED'
  const hasLinkedInvoice = payment.invoiceStatus === 'INVOICE_PRESENT'
  const hasLinkedCost = (payment.linkedIndirectCostCount ?? 0) > 0

  const invalidateViews = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['payments'] })
    void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
    void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-invoices', payment.id] })
  }

  const invoiceRequirementMutation = useMutation({
    mutationFn: async (notNeeded: boolean) =>
      overrideUniversalTransaction(payment.id, {
        invoiceStatus: notNeeded ? 'INVOICE_NOT_NEEDED' : 'NO_INFO',
      }),
    onSuccess: invalidateViews,
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : t('requirementsPopover.toasts.updateInvoiceError'))
    },
  })

  const costRequirementMutation = useMutation({
    mutationFn: async (notNeeded: boolean) =>
      updatePaymentLinkingRequirement(payment.id, notNeeded ? 'NOT_NEEDED' : 'REQUIRED'),
    onSuccess: invalidateViews,
    onError: (error: unknown) => {
      const status = (
        error as { response?: { status?: number } } | null
      )?.response?.status
      if (status === 409) {
        toast.error(t('requirementsPopover.toasts.unlinkCostFirst'))
        return
      }
      toast.error(error instanceof Error ? error.message : t('requirementsPopover.toasts.updateCostError'))
    },
  })

  const isInvoicePending = invoiceRequirementMutation.isPending
  const isCostPending = costRequirementMutation.isPending
  const disableSetCostNotNeeded = hasLinkedCost && !costLinkNotNeeded

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'h-7 w-7 min-w-0 border border-transparent bg-transparent text-muted-foreground/75 hover:border-border/50 hover:bg-muted/15 hover:text-muted-foreground',
            className,
          )}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          aria-label={t('requirementsPopover.triggerAria')}
        >
          <EllipsisVertical className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-3" align="end" sideOffset={6}>
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-foreground">{t('requirementsPopover.invoiceNotNeeded')}</span>
              <Switch
                checked={invoiceNotNeeded}
                disabled={isInvoicePending || hasLinkedInvoice}
                onCheckedChange={checked => {
                  invoiceRequirementMutation.mutate(checked)
                }}
                aria-label={t('requirementsPopover.invoiceToggleAria')}
              />
            </div>
            {hasLinkedInvoice ? (
              <p className="text-[11px] text-muted-foreground/70">{t('requirementsPopover.unlinkInvoiceFirst')}</p>
            ) : null}
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-foreground">{t('requirementsPopover.costLinkNotNeeded')}</span>
              <Switch
                checked={costLinkNotNeeded}
                disabled={isCostPending || disableSetCostNotNeeded}
                onCheckedChange={checked => {
                  costRequirementMutation.mutate(checked)
                }}
                aria-label={t('requirementsPopover.costToggleAria')}
              />
            </div>
            {disableSetCostNotNeeded ? (
              <p className="text-[11px] text-muted-foreground/70">{t('requirementsPopover.unlinkCostFirst')}</p>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
